/* ==========================================================================
   Craftly — core theme behaviour
   ========================================================================== */
(function () {
  'use strict';

  var overlay = null;
  var openPanels = [];

  function getOverlay() {
    if (!overlay) overlay = document.querySelector('[data-overlay]');
    return overlay;
  }

  function lockScroll(lock) {
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  function showOverlay() {
    var el = getOverlay();
    if (!el) return;
    el.hidden = false;
    requestAnimationFrame(function () {
      el.classList.add('is-visible');
    });
  }

  function hideOverlay() {
    var el = getOverlay();
    if (!el) return;
    el.classList.remove('is-visible');
    setTimeout(function () {
      if (!openPanels.length) el.hidden = true;
    }, 300);
  }

  /* ---------- Panel system (drawers, search, cart) ---------- */

  var Panel = {
    open: function (el, trigger) {
      if (!el) return;
      // Drop panels that were removed from the DOM (e.g. re-rendered cart drawer)
      openPanels = openPanels.filter(function (p) { return document.body.contains(p); });
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      if (openPanels.indexOf(el) === -1) openPanels.push(el);
      el._trigger = trigger || document.activeElement;
      showOverlay();
      lockScroll(true);
      var focusable = el.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) setTimeout(function () { focusable.focus(); }, 120);
      document.dispatchEvent(new CustomEvent('panel:open', { detail: { panel: el } }));
    },
    close: function (el) {
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      openPanels = openPanels.filter(function (p) { return p !== el; });
      if (!openPanels.length) {
        hideOverlay();
        lockScroll(false);
      }
      if (el._trigger && typeof el._trigger.focus === 'function') el._trigger.focus();
    },
    closeAll: function () {
      openPanels.slice().forEach(function (p) { Panel.close(p); });
    }
  };

  window.ThemePanel = Panel;

  document.addEventListener('click', function (event) {
    var openTrigger = event.target.closest('[data-panel-open]');
    if (openTrigger) {
      var target = document.querySelector(openTrigger.getAttribute('data-panel-open'));
      if (target) {
        event.preventDefault();
        Panel.open(target, openTrigger);
      }
      return;
    }

    var closeTrigger = event.target.closest('[data-panel-close]');
    if (closeTrigger) {
      event.preventDefault();
      var panel = closeTrigger.closest('.cart-drawer, .menu-drawer, .search-modal');
      Panel.close(panel);
      return;
    }

    if (event.target.hasAttribute && event.target.hasAttribute('data-overlay')) {
      Panel.closeAll();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && openPanels.length) {
      Panel.close(openPanels[openPanels.length - 1]);
    }
  });

  /* ---------- Sticky header shadow ---------- */

  var headerWrapper = document.querySelector('.header-wrapper');
  if (headerWrapper) {
    var onScroll = function () {
      headerWrapper.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Announcement marquee: duplicate track for seamless loop ---------- */

  function initMarquee() {
    document.querySelectorAll('[data-marquee]').forEach(function (marquee) {
      if (marquee.dataset.marqueeReady === 'true') return;
      var track = marquee.querySelector('.announcement-bar__track');
      if (!track) return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      marquee.appendChild(clone);
      marquee.dataset.marqueeReady = 'true';
    });
  }

  /* ---------- Scroll reveal ---------- */

  function initReveal() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    document.querySelectorAll('.reveal:not(.is-visible)').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------- Countdown ---------- */

  function initCountdown() {
    document.querySelectorAll('[data-countdown]').forEach(function (el) {
      if (el.dataset.countdownReady === 'true') return;
      el.dataset.countdownReady = 'true';

      var end = new Date(el.getAttribute('data-countdown')).getTime();
      if (isNaN(end)) return;

      var fields = {
        days: el.querySelector('[data-countdown-days]'),
        hours: el.querySelector('[data-countdown-hours]'),
        minutes: el.querySelector('[data-countdown-minutes]'),
        seconds: el.querySelector('[data-countdown-seconds]')
      };
      var timer = el.querySelector('[data-countdown-timer]');
      var expired = el.querySelector('[data-countdown-expired]');

      var pad = function (n) { return String(n).padStart(2, '0'); };

      var tick = function () {
        var diff = end - Date.now();
        if (diff <= 0) {
          if (timer) timer.hidden = true;
          if (expired) expired.hidden = false;
          clearInterval(interval);
          return;
        }
        var seconds = Math.floor(diff / 1000);
        if (fields.days) fields.days.textContent = pad(Math.floor(seconds / 86400));
        if (fields.hours) fields.hours.textContent = pad(Math.floor((seconds % 86400) / 3600));
        if (fields.minutes) fields.minutes.textContent = pad(Math.floor((seconds % 3600) / 60));
        if (fields.seconds) fields.seconds.textContent = pad(seconds % 60);
      };

      tick();
      var interval = setInterval(tick, 1000);
    });
  }

  /* ---------- Slideshow ---------- */

  function initSlideshow() {
    document.querySelectorAll('[data-slideshow]').forEach(function (el) {
      if (el.dataset.slideshowReady === 'true') return;
      el.dataset.slideshowReady = 'true';

      var slides = Array.prototype.slice.call(el.querySelectorAll('[data-slide]'));
      var dots = Array.prototype.slice.call(el.querySelectorAll('[data-slide-dot]'));
      if (slides.length < 2) return;

      var index = 0;
      var interval = parseInt(el.getAttribute('data-slideshow-interval'), 10) || 0;
      var timer = null;

      var go = function (next) {
        slides[index].classList.remove('is-active');
        if (dots[index]) dots[index].setAttribute('aria-current', 'false');
        index = (next + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        if (dots[index]) dots[index].setAttribute('aria-current', 'true');
      };

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          go(i);
          if (timer) {
            clearInterval(timer);
            timer = setInterval(function () { go(index + 1); }, interval);
          }
        });
      });

      if (interval > 0) {
        timer = setInterval(function () { go(index + 1); }, interval);
      }
    });
  }

  /* ---------- Predictive search ---------- */

  function initPredictiveSearch() {
    document.querySelectorAll('[data-predictive-search]').forEach(function (form) {
      if (form.dataset.predictiveReady === 'true') return;
      form.dataset.predictiveReady = 'true';

      var input = form.querySelector('input[type="search"]');
      var results = document.querySelector(form.getAttribute('data-results-target'));
      if (!input || !results) return;

      var debounce = null;

      var fetchResults = function () {
        var term = input.value.trim();
        if (term.length < 2) {
          results.innerHTML = '';
          return;
        }
        var url =
          window.Theme.routes.predictiveSearch +
          '?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product,collection,article,page&resources[limit]=6&section_id=predictive-search';

        fetch(url)
          .then(function (response) { return response.text(); })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var content = doc.querySelector('#shopify-section-predictive-search');
            results.innerHTML = content ? content.innerHTML : '';
          })
          .catch(function () {
            results.innerHTML = '';
          });
      };

      input.addEventListener('input', function () {
        clearTimeout(debounce);
        debounce = setTimeout(fetchResults, 250);
      });
    });
  }

  /* ---------- Share button ---------- */

  function initShare() {
    document.querySelectorAll('[data-share-button]').forEach(function (button) {
      if (button.dataset.shareReady === 'true') return;
      button.dataset.shareReady = 'true';

      button.addEventListener('click', function () {
        var url = button.getAttribute('data-share-url') || window.location.href;
        var title = button.getAttribute('data-share-title') || document.title;

        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () {
            var original = button.textContent;
            button.textContent = button.getAttribute('data-copied-text') || 'Link copied';
            setTimeout(function () { button.textContent = original; }, 2000);
          });
        }
      });
    });
  }

  /* ---------- Facets: auto-submit ---------- */

  function initFacets() {
    document.querySelectorAll('[data-facet-form]').forEach(function (form) {
      if (form.dataset.facetReady === 'true') return;
      form.dataset.facetReady = 'true';

      form.addEventListener('change', function (event) {
        if (event.target.matches('input[type="checkbox"], select')) {
          form.submit();
        }
      });
    });

    document.querySelectorAll('[data-sort-select]').forEach(function (select) {
      if (select.dataset.sortReady === 'true') return;
      select.dataset.sortReady = 'true';
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  /* ---------- Close details panels on outside click ---------- */

  document.addEventListener('click', function (event) {
    document.querySelectorAll('details.facets__disclosure[open]').forEach(function (details) {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  /* ---------- Init ---------- */

  function init() {
    initMarquee();
    initReveal();
    initCountdown();
    initSlideshow();
    initPredictiveSearch();
    initShare();
    initFacets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
  document.addEventListener('theme:reinit', init);
})();
